import { intersection, omit, snakeCase } from 'lodash';
import moment from 'moment';
import * as trackingApi from 'Tracking/tracking-api';
import * as trackingStorageApi from 'Tracking/tracking-storage-api';
import { BE_TRACKING } from 'Tracking/constants';
import type { ProposalsByIdQueryParam } from 'Views/booking/BookingEngine.query';
import { proposalsByIdQuery } from 'Views/booking/BookingEngine.query';
import {
  PAGE_BE_ACCOMMODATION,
  getBookingEngineUri,
} from 'Utils/booking-engine-pages/BookingPagesUtils';
import { redirectToOidc } from 'Utils/uri/redirectUtils';
import { MAX_PAX_SELLER, MAX_PAX } from 'Utils/faceting/constants';
import { timestampToApiDate } from 'Utils/date/apiDate';
import { hasFetchProposalsFailed, areAnyTimeSlotIdsPresent } from 'Utils/proposal/proposalUtils';
import type {
  ServiceMutationType,
  OverloadForSpecificProposalIdsType,
} from 'Utils/graphql-query-helper/booking/proposal/proposalQuery';
import {
  addAccommodationMutation,
  addTransportMutation,
  fetchPayment,
  proposalsQuery,
  promoCodeProposalMutation,
  autosaveProposalMutation,
  putServicesMutation,
  deleteServicesMutation,
  switchProposalsOffers as switchProposalsOffersMutation,
  addServiceMutation,
  refreshProposal,
} from 'Utils/graphql-query-helper/booking/proposal/proposalQuery';
import {
  getSigninURIWithRedirection,
  patchProposal,
  saveCustomerProposal,
  saveCustomerProposalOpportunityId,
} from 'Utils/seller/sellerUtils';
import type { updateAttendeesMutationType } from 'Utils/graphql-query-helper/booking/attendees/attendeesQuery';
import { updateAttendeesMutation } from 'Utils/graphql-query-helper/booking/attendees/attendeesQuery';
import type { BookingEngineEntry } from 'Store/booking/constants';
import { BOOKING_ENGINE_ENTRIES } from 'Store/booking/constants';
import {
  proposalsSelector,
  getCurrentProposal,
  getSelectedProposalId,
} from 'Store/booking/booking.selector';
import { getArrivalDate } from 'Store/trip-date-criteria/storeHelper';
import { DEFAULT_ADULTS_NB } from 'Store/trip-information/adults/constants';
import { getKids, getAdults } from 'Store/trip-information/storeHelper';
import createAction, { createOptimisticAction } from 'Store/utils/createAction';
import { endLoading, startLoading } from 'Store/global-loader/globalLoader.action';
import logger from 'Logger/logger';
import { isFeatureFlipped } from 'Utils/feature-flipping/featureFlippingUtils';
import { getBookingEngineI18n } from 'Store/cms/cms.actions';
import * as pleUtils from 'Utils/ple-utils';
import { STATUS_CODE_400 } from 'Utils/status-code/statusCode.constants';
import { getAllAttendeesIds } from 'Components/widgets/contact-info/ContactInfoUtils';
import { selectAttendeesWithInfosByHouseholds } from 'Store/booking/attendees/attendees.selector';
import {
  ADD_ACCOMMODATION,
  ADD_TRANSPORT,
  ADD_SERVICE,
  CLEAR_PROPOSALS,
  DELETE_SERVICE,
  FETCH_PROPOSALS,
  FETCH_PROPOSALS_BY_ID,
  FETCH_SELECTED_PROPOSAL_PAYMENT,
  SAVE_CUSTOMER_PROPOSAL,
  PATCH_PROPOSAL,
  SAVE_CUSTOMER_PROPOSAL_OPPORTUNITYID,
  UPDATE_INSURANCES,
  DELETE_INSURANCES,
  PREPARE_BE,
  DISPLAY_TOAST_ON_CONTEXT_SHARE,
  FETCH_PROPOSALS_FROM_EDIT_CRITERIA,
  SAVE_DONATION_TO_PROPOSALS,
  SET_PROMO_CODE,
  REMOVE_PROMO_CODE,
  AUTOSAVE,
  UPDATE_ATTENDEES,
  UPDATE_SERVICES,
  SWITCH_PROPOSALS_OFFERS,
  INSURANCE_ADDED,
  INSURANCE_DELETED,
  PROPOSAL_REFRESH,
} from './constants';
export type FetchProposalsParams = {
  departureCity: string;
  duration: number;
  isJoyView?: boolean;
  isUrbanOasis?: boolean;
  productId: string;
  globalLoaderLabel?: string;
  adultsAttendeesIds?: Array<string>;
  kidsAttendeesIds?: Array<string>;
  newArrivalDate?: string;
  bookingEngineEntry: BookingEngineEntry;
};
const BOOKING_ENGINE_ENTRY_TRACKING_LABELS = {
  [BOOKING_ENGINE_ENTRIES.PRICE_CALENDAR]: 'calendarPrice',
  [BOOKING_ENGINE_ENTRIES.PRICE_REMOTE]: 'remotePrice',
  [BOOKING_ENGINE_ENTRIES.QUICK_SEARCH]: 'quickSearch',
  [BOOKING_ENGINE_ENTRIES.CRUISE_BOOKING]: 'cruiseBooking',
};

function throwFormatError(data) {
  const error = Object.assign(new Error(), data);
  throw error;
}

function applyProposalPromoCode(
  proposalId: string,
  params: {
    promoCode: string;
    partnerId?: string;
    accessCode?: string;
    customerId?: number;
  },
  appContext: AppContext,
) {
  return createOptimisticAction(
    SET_PROMO_CODE,
    {},
    {
      optimist: true,
      promise: promoCodeProposalMutation({ ...params, proposalId, type: 'apply' }, appContext),
    },
  );
}

function removeProposalPromoCode(
  proposalId: string,
  params: {
    promoCode: string;
  },
  appContext: AppContext,
) {
  return createOptimisticAction(
    REMOVE_PROMO_CODE,
    {},
    {
      optimist: true,
      promise: promoCodeProposalMutation({ ...params, proposalId, type: 'remove' }, appContext),
    },
  );
}

function setProposalAsAutosaved(proposalId: string, appContext: AppContext) {
  return createOptimisticAction(
    AUTOSAVE,
    {},
    {
      optimist: true,
      promise: autosaveProposalMutation(
        {
          proposalId,
        },
        appContext,
      ),
    },
  );
}

function fetchProposalsById({
  proposalsId,
  appContext,
  shouldRequestPayment,
  adultsAttendeesIds,
  kidsAttendeesIds,
}: ProposalsByIdQueryParam & {
  appContext: AppContext;
}) {
  return (dispatch: ReduxDispatch, getState: GetReduxState) => {
    return dispatch(
      createOptimisticAction(
        FETCH_PROPOSALS_BY_ID,
        {},
        {
          optimist: true,
          promise: proposalsByIdQuery(
            {
              proposalsId,
              shouldRequestPayment,
              adultsAttendeesIds,
              kidsAttendeesIds,
              fetchActivityCategories: isFeatureFlipped(appContext)('booking.displayActivities'),
              selectedProposalId: getSelectedProposalId(getState()),
            },
            appContext,
          ),
        },
      ),
    );
  };
}

export function getNumberOfAttendees(nbAdults: number = 0, nbKids: number = 0, isSeller: boolean) {
  const numberOfAttendees: number = nbAdults + nbKids;
  const maxPaxByUser: number = isSeller ? MAX_PAX_SELLER : MAX_PAX;

  /** reset to default adults number if no adults or if the number exceeds the allowed sum (maxPaxByUser) */
  return numberOfAttendees === 0 || numberOfAttendees > maxPaxByUser
    ? DEFAULT_ADULTS_NB
    : numberOfAttendees;
}
type CriteriaType = {
  proposal: Proposal;
  departureCity: string;
  flightClass?: string;
  directFlights?: boolean;
};

function getTransportSearchCriteria({
  proposal,
  flightClass,
  directFlights = false,
  departureCity,
}: CriteriaType): TransportSearchCriteria {
  if (!proposal.transports) {
    return {};
  }

  const { inbound, outbound } = proposal.transports;

  function formatTransportDetail(transportDetail, way): TransportSearchJourney {
    const { arrivalDate, departureDate } = proposal;
    const firstTravelSection = transportDetail.travelSections[0];
    const lastTravelSection =
      transportDetail.travelSections[transportDetail.travelSections.length - 1];
    return {
      way: way,
      travelSections: [
        {
          departure: {
            city: {
              id:
                way === 'INBOUND'
                  ? departureCity || firstTravelSection.departure.cityId
                  : firstTravelSection.departure.cityId,
            },
            date: way === 'INBOUND' ? '' : departureDate,
            location: {
              id: firstTravelSection.departure.id,
              terminalCode: firstTravelSection.departure.terminalCode,
            },
          },
          arrival: {
            city: {
              id:
                way === 'INBOUND'
                  ? lastTravelSection.arrival.cityId
                  : departureCity || lastTravelSection.arrival.cityId,
            },
            date: way === 'INBOUND' ? arrivalDate : '',
            location: {
              id: lastTravelSection.arrival.id,
              terminalCode: lastTravelSection.arrival.terminalCode,
            },
          },
        },
      ],
    };
  }

  return {
    journeys: [
      formatTransportDetail(inbound, 'INBOUND'),
      formatTransportDetail(outbound, 'OUTBOUND'),
    ],
    transportClass: flightClass || inbound?.travelSections[0].transport?.class,
    isDirectFlightOnly: directFlights,
    maxConnections: directFlights ? 0 : undefined,
    transportConnectionDuration: undefined,
    isTransportStationsChangesAllowed: undefined,
    isCabinMixingAllowed: undefined,
    isCompaniesMixingAllowed: undefined,
    imposedCompanies: undefined,
    imposedConnectionCities: undefined,
    excludedCompanies: undefined,
    excludedConnectionCities: undefined,
  };
}

/*
  ATTENTION:
  Les valeurs par défaut de departureCity et duration sont mises dans le CMS,
  on ne peut donc pas récupérer les valeurs du store
 */
function fetchProposalsAndRedirect(
  params: FetchProposalsParams,
  appContext: AppContext,
  router: any,
  pageType: string = 'Booking Engine',
) {
  return (dispatch: ReduxDispatch, getState: GetReduxState) => {
    const state = getState();
    const intArrivalDate =
      params.newArrivalDate ||
      getArrivalDate(state) ||
      timestampToApiDate(state.tripCriteria.departureDate);

    if (!intArrivalDate) {
      logger.error('the arrivalDate is required in fetchProposalsAndRedirect action');
      return;
    }

    const queryParams = omit(router.query, ['dynamicPage']);
    const adultsAttendees = getAdults(state) || [];
    const kidsAttendees = getKids(state) || [];
    const nbAdults: number = appContext.isSeller
      ? Number(queryParams.number_of_adults || adultsAttendees.length)
      : adultsAttendees.length;
    const nbKids: number = appContext.isSeller
      ? queryParams.kids?.split('_')?.length || 0
      : kidsAttendees.length;
    const allowedAttendeesNumber = getNumberOfAttendees(nbAdults, nbKids, appContext.isSeller);
    const proposalParams = {
      arrivalDate: intArrivalDate.toString(),
      kids: kidsAttendees,
      adults: adultsAttendees,
      numberAttendees: allowedAttendeesNumber,
      fetchActivityCategories: isFeatureFlipped(appContext)('booking.displayActivities'),
      ...(appContext.isSeller
        ? {
            salesNetworkId: queryParams.cxo_sales_network_id,
          }
        : null),
      ...params,
    };
    const redirectUri = getSigninURIWithRedirection({
      locale: appContext.locale,
      proposalId: null,
      step: null,
      isCmta: appContext.isCmta,
    });

    /**  Method 5 steps
     * 1. fetch BE i18n config
     * 2. fetch proposal
     * 3. send tracking
     * 4. Set search criteria if transport for MoreTransports
     * 5. redirect to signin redirection URL
     */

    /* 1. fetch BE i18n config */
    dispatch(getBookingEngineI18n(appContext));

    /* 2. fetch proposal */
    return dispatch(
      createOptimisticAction(
        FETCH_PROPOSALS,
        {
          globalLoaderLabel: params.globalLoaderLabel,
          bookingEngineEntry: params.bookingEngineEntry,
        },
        {
          optimist: true,
          promise: proposalsQuery(proposalParams, appContext).then((response) => {
            const { proposals } = response;
            const isFetchProposalsFailed = hasFetchProposalsFailed(proposals);

            if (!isFetchProposalsFailed) {
              const bookingUrl = getBookingEngineUri(
                PAGE_BE_ACCOMMODATION,
                proposals.proposals || [],
                proposals.resort,
                undefined,
                queryParams,
              );

              /* 5. redirect to signin redirection URL */
              router.push(bookingUrl);
            }

            const errorCode = proposals?.errors?.[0]?.errorCode;
            trackingApi.setDataLayer(
              {
                categoryGA: 'resort',
                actionGA: BOOKING_ENGINE_ENTRY_TRACKING_LABELS[params.bookingEngineEntry],
                labelGA: isFetchProposalsFailed
                  ? `errorCheckPrices${errorCode ? `_${snakeCase(errorCode).toUpperCase()}` : ''}`
                  : 'checkPrices',
                siteSection: `Shopping_${pageType}`,
              },
              appContext,
            );
            return response;
          }),
          redirect_uri: redirectUri,
        },
      ),
    );
  };
}

export function handleError(errors: any, appContext: AppContext) {
  // We don't do anything specific with the errors, but this will let us display per field errors
  logger.error(errors);
  const error = errors.errors
    ? errors
    : {
        errors,
      };

  // Redirect to oidc if the statusCode is 401
  if (pleUtils.hasAuthResponseError(error)) {
    redirectToOidc(appContext);
    return;
  }

  // Some errors are properly handled by graphql. But we don't handle them explicitly
  // We rely on the message to know the source of the error because of babel < 7
  throwFormatError({
    ...error,
    statusCode:
      error.message === 'Unhandled field form error'
        ? STATUS_CODE_400
        : error?.errors?.[0]?.statusCode,
  });
}

function editCriteriaFetchProposals(
  criteria: any,
  proposalsLoderLabel: string,
  appContext: AppContext,
  queryParams: ParsedUrlQuery,
) {
  return (dispatch: ReduxDispatch, getState: GetReduxState) => {
    const { search, departure, attendees, departureCity } = criteria;
    const payload = {
      globalLoaderLabel: proposalsLoderLabel,
    };
    const state = getState();
    const {
      attendees: { adults: adultsAttendees, children: kidsAttendees },
    } = selectAttendeesWithInfosByHouseholds(state);
    const adults = new Array(attendees.adults).fill({}).map((_, index) => ({
      neolid: (adultsAttendees[index] || {}).customerId,
    }));
    const kids = attendees.kids.map((kid) => {
      const kidFromStore = kidsAttendees?.find((kidAtt) => kidAtt.birthdate === kid.birthdate);
      return {
        neolid: kidFromStore?.customerId,
        birthdate: kid.birthdate,
      };
    });
    return dispatch(
      createOptimisticAction(FETCH_PROPOSALS_FROM_EDIT_CRITERIA, payload, {
        optimist: true,
        promise: proposalsQuery(
          {
            productId: search.value,
            arrivalDate: moment(departure.date).format('YYYYMMDD'),
            duration: departure.duration,
            departureCity: departureCity.value,
            isJoyView: criteria.isJoyView,
            isUrbanOasis: criteria.isUrbanOasis,
            kids,
            adults,
            numberAttendees: getNumberOfAttendees(
              attendees.adults,
              attendees?.kids?.length || 0,
              appContext.isSeller,
            ),
            fetchActivityCategories: isFeatureFlipped(appContext)('booking.displayActivities'),
            ...(appContext.isSeller
              ? {
                  salesNetworkId: queryParams.cxo_sales_network_id,
                }
              : null),
          },
          appContext,
        ),
      }),
    );
  };
}

type SellerParams = {
  salesNetworkId: any;
  adultsAttendeesIds?: string[];
  kidsAttendeesIds?: string[];
};

function simpleFetchProposals(
  criterias: {
    productId: string;
    arrivalDate: string;
    duration: number;
    attendees: {
      adults: Array<any>;
      kids: Array<any>;
    };
    departureCity: string;
    bookingEngineEntry: BookingEngineEntry;
  },
  proposalsLoaderLabel: string,
  appContext: AppContext,
  location: BookingRouterLocation,
) {
  const { productId, arrivalDate, duration, attendees, departureCity, bookingEngineEntry } =
    criterias;
  const payload = {
    globalLoaderLabel: proposalsLoaderLabel,
    bookingEngineEntry,
  };
  const sellerParams: SellerParams = {
    ...getAllAttendeesIds({
      adults: attendees.adults,
      kids: attendees.kids,
    }),
    salesNetworkId: location.queryParams.cxo_sales_network_id,
  };
  return createOptimisticAction(FETCH_PROPOSALS, payload, {
    optimist: true,
    promise: proposalsQuery(
      {
        ...(appContext.isSeller ? sellerParams : null),
        productId,
        arrivalDate,
        duration,
        departureCity,
        numberAttendees: attendees.adults.length + attendees.kids.length,
        adults: attendees.adults,
        kids: attendees.kids,
        fetchActivityCategories: isFeatureFlipped(appContext)('booking.displayActivities'),
      },
      appContext,
    ),
  });
}

function putTransportAndDispatchProposals(
  transportId: string,
  proposalsId: Array<string>,
  isJoyview: boolean,
  isUrbanOasis: boolean,
  appContext: AppContext,
  globalLoaderLabel: string,
  toastLabel: string,
) {
  return (dispatch: ReduxDispatch): any => {
    // TODO show a toast if error
    return dispatch(
      createOptimisticAction(
        ADD_TRANSPORT,
        {
          globalLoaderLabel,
          toastLabel,
          transportId,
        },
        {
          optimist: true,
          promise: addTransportMutation(
            transportId,
            proposalsId,
            isJoyview,
            isUrbanOasis,
            appContext,
          ),
        },
      ),
    );
  };
}

function fetchRefreshProposal(proposalId: string, salesNetworkId?: string, appContext: AppContext) {
  return createOptimisticAction(
    PROPOSAL_REFRESH,
    {},
    {
      optimist: true,
      promise: refreshProposal(proposalId, salesNetworkId, appContext),
    },
  );
}

function displayToastOnContextShare(toastLabel: string) {
  return {
    type: DISPLAY_TOAST_ON_CONTEXT_SHARE,
    payload: {
      toastLabel,
    },
  };
}

export type AddAccommodationArgs = {
  arrangement: AccommodationArrangement;
  proposalsId: Array<string>;
  isJoyview: boolean;
  isUrbanOasis: boolean;
  appContext: AppContext;
  globalLoaderLabel: string;
  toastLabel: string;
  reduxAction?: string;
};

function addAccommodation({
  arrangement,
  proposalsId,
  isJoyview,
  isUrbanOasis,
  appContext,
  globalLoaderLabel,
  toastLabel,
  reduxAction = ADD_ACCOMMODATION,
}: AddAccommodationArgs) {
  return (dispatch: ReduxDispatch): any => {
    return dispatch(
      createOptimisticAction(
        reduxAction,
        {
          globalLoaderLabel,
          toastLabel,
        },
        {
          optimist: true,
          promise: addAccommodationMutation(
            arrangement,
            proposalsId,
            isJoyview,
            isUrbanOasis,
            appContext,
          ),
        },
      ),
    );
  };
}

function updateAttendees({ proposalId, attendees, appContext }: updateAttendeesMutationType) {
  return (dispatch: ReduxDispatch): any => {
    dispatch(startLoading());
    return updateAttendeesMutation({
      proposalId,
      attendees,
      appContext,
    })
      .then(
        ({
          modifyAttendees,
        }: {
          modifyAttendees: {
            proposal: Proposal;
            errors: any;
            attendees: any;
          };
        }) => {
          const { errors, proposal, attendees: modifiedAttendees } = modifyAttendees;

          if (errors) {
            throwFormatError({
              errors,
            });
          }

          const cookieToSet = {
            ...trackingStorageApi.get(BE_TRACKING),
            neolaneId: proposal?.attendees?.list?.[0]?.customerId,
            BE_customerStatus: modifiedAttendees?.[0]?.customerType,
          };
          trackingStorageApi.set(cookieToSet);
          dispatch(createAction(UPDATE_ATTENDEES, modifyAttendees));
        },
      )
      .catch((error) => handleError(error, appContext))
      .finally(() => dispatch(endLoading()));
  };
}

function refuseInsuranceAction(insuranceId: string) {
  return (dispatch: ReduxDispatch): any => {
    dispatch(createAction(INSURANCE_DELETED, insuranceId));
  };
}

function clearProposals() {
  return createAction(CLEAR_PROPOSALS);
}

const fetchSelectedProposalPayment = (
  selectedProposal: Readonly<Proposal>,
  appContext: AppContext,
) => {
  return createOptimisticAction(
    FETCH_SELECTED_PROPOSAL_PAYMENT,
    {
      selectedProposalId: selectedProposal.id,
    },
    {
      optimist: true,
      promise: fetchPayment(selectedProposal.id, appContext),
    },
  );
};

const saveCustomerProposalsHandler = (
  proposalId: string,
  appContext: AppContext,
  customerId: string,
) => {
  return (dispatch: ReduxDispatch): any =>
    dispatch(
      createOptimisticAction(
        SAVE_CUSTOMER_PROPOSAL,
        {},
        {
          optimist: true,
          promise: saveCustomerProposal(proposalId, appContext, customerId),
        },
      ),
    );
};

const patchProposalHandler = (proposalId: string, appContext: AppContext, extend: boolean) => {
  const options = {
    operation: 'extend_persistence',
    parameters: extend.toString(),
  };
  return (dispatch: ReduxDispatch): any =>
    dispatch(
      createOptimisticAction(
        PATCH_PROPOSAL,
        {},
        {
          optimist: true,
          promise: patchProposal(proposalId, appContext, options.operation, options.parameters),
        },
      ),
    );
};

const saveCustomerProposalsOpportunityIdHandler = (
  proposalId: string,
  appContext: AppContext,
  customerId: string,
  opportunityId: string,
) => {
  return (dispatch: ReduxDispatch): any =>
    dispatch(
      createOptimisticAction(
        SAVE_CUSTOMER_PROPOSAL_OPPORTUNITYID,
        {},
        {
          optimist: true,
          promise: saveCustomerProposalOpportunityId(
            proposalId,
            appContext,
            customerId,
            opportunityId,
          ),
        },
      ),
    );
};

const addOrReplaceAdditionalServices = ({
  attendees,
  showNotification = false,
  appContext,
}: {
  attendees: ServiceMutationType;
  showNotification?: boolean;
  appContext: AppContext;
}) => {
  return (dispatch: ReduxDispatch, getState: GetReduxState) => {
    const proposal = getCurrentProposal(getState());
    const proposals: Proposal[] = proposalsSelector(getState());
    const servicesIds = attendees.map(({ productInfoId }) => productInfoId);
    // keep only proposals that have thoses services ids
    const proposalsWithServices = proposals.filter((p) => {
      const additionalServicesValues = Object.values(p.additionalServices);
      return additionalServicesValues?.some(
        (services) =>
          Array.isArray(services) &&
          services?.some(({ pricingInfo }) => servicesIds.includes(pricingInfo?.id)),
      );
    });
    const proposalsWithServicesInAttendees = proposals.filter(({ additionalServices }) =>
      Object.values(additionalServices)?.some((services) => {
        if (Array.isArray(services)) {
          return services?.some((typedServices: any) => {
            return areAnyTimeSlotIdsPresent(
              typedServices?.pricingInfo.attendees || [],
              servicesIds,
            );
          });
        }
      }),
    );
    const validProposalIds = (
      proposalsWithServices.length > 0 ? proposalsWithServices : proposalsWithServicesInAttendees
    ).map(({ id }) => id);
    const additionalServicesValues = Object.values(proposal.additionalServices);
    const isUpdatingAlreadyReservedService = additionalServicesValues.some((oldServices) =>
      oldServices.some(
        ({ pricingInfo }) => servicesIds.includes(pricingInfo.id) && pricingInfo.isPartiallyRented,
      ),
    );
    const newReservedServiceToastLabel = additionalServicesValues.some((oldServices) =>
      oldServices.some(
        ({ pricingInfo }) => servicesIds.includes(pricingInfo.id) && pricingInfo.type === 'RENTAL',
      ),
    )
      ? 'booking.activities.skiEquipment.newEquipmentSelection'
      : 'booking.activities.newActivitySelection';
    return dispatch(
      createOptimisticAction(
        UPDATE_SERVICES,
        showNotification
          ? {
              toastLabel: isUpdatingAlreadyReservedService
                ? 'booking.activities.activityUpdate'
                : newReservedServiceToastLabel,
            }
          : {},
        {
          optimist: true,
          promise: putServicesMutation(validProposalIds, attendees, appContext),
        },
      ),
    );
  };
};

const deleteAdditionalServices = ({
  serviceIds,
  attendeeIds,
  showNotification = false,
  overloadForSpecificProposalIds,
  appContext,
  proposals: proposalsParam,
}: {
  serviceIds: ReadonlyArray<string>;
  attendeeIds: ReadonlyArray<string> | string;
  showNotification?: boolean;
  overloadForSpecificProposalIds?: OverloadForSpecificProposalIdsType;
  appContext: AppContext;
  proposals?: ReadonlyArray<Proposal>;
}) => {
  return (dispatch: ReduxDispatch, getState: GetReduxState) => {
    const proposals = proposalsParam || proposalsSelector(getState());
    // keep only proposals that have thoses services ids
    const proposalsWithServices = proposals.filter(({ additionalServices }) =>
      Object.values(additionalServices)?.some((services) => {
        if (Array.isArray(services)) {
          return services?.some((typedServices) => {
            const schedules = typedServices?.pricingInfo?.schedules || [];
            const slots = Object.values(schedules[0].slots || {});
            const slotsIds = slots?.map(({ id }) => id);
            return (
              serviceIds.includes(typedServices?.pricingInfo?.id) ||
              (intersection(slotsIds, serviceIds) || []).length > 0
            );
          });
        }
      }),
    );
    const proposalsWithServicesInAttendees = proposals.filter(({ additionalServices }) =>
      Object.values(additionalServices)?.some((services) => {
        if (Array.isArray(services)) {
          return services?.some((typedServices) => {
            // @ts-expect-error TODO(flow-migration)
            return areAnyTimeSlotIdsPresent(typedServices?.pricingInfo.attendees || [], serviceIds);
          });
        }
      }),
    );
    const validProposalIds = (
      proposalsWithServices.length > 0 ? proposalsWithServices : proposalsWithServicesInAttendees
    ).map(({ id }) => id);
    return dispatch(
      createOptimisticAction(
        DELETE_SERVICE,
        showNotification
          ? {
              toastLabel: 'booking.activities.activityUpdate',
            }
          : {},
        {
          optimist: true,
          promise: deleteServicesMutation({
            proposalIds: validProposalIds,
            serviceIds,
            attendeeIds,
            overloadForSpecificProposalIds,
            appContext,
          }),
        },
      ),
    );
  };
};

const addAdditionalService = ({
  proposalIds,
  serviceId,
  attendeeIds,
  schedules,
  showNotification = false,
  overloadForSpecificProposalIds,
  appContext,
}: {
  proposalIds: ReadonlyArray<string>;
  serviceId: string;
  attendeeIds: ReadonlyArray<string> | string;
  schedules: ReadonlyArray<{
    startDate: string;
    endDate: string;
  }>;
  showNotification?: boolean;
  overloadForSpecificProposalIds?: OverloadForSpecificProposalIdsType;
  appContext: AppContext;
}) => {
  return (dispatch: ReduxDispatch) =>
    dispatch(
      createOptimisticAction(
        ADD_SERVICE,
        showNotification
          ? {
              toastLabel: 'booking.activities.activityUpdate',
            }
          : {},
        {
          optimist: true,
          promise: addServiceMutation({
            proposalIds,
            serviceId,
            attendeeIds,
            schedules,
            overloadForSpecificProposalIds,
            appContext,
          }),
        },
      ),
    );
};

/**
INSURANCE ADD/DELETE
* */
export type AddOrRemoveInsuranceFromProposal = {
  insurance: UpsellInsurance;
  attendeeIds: Array<string>;
  appContext: AppContext;
};

const addInsurances = ({
  insurance,
  attendeeIds,
  appContext,
}: AddOrRemoveInsuranceFromProposal) => {
  const { id, schedules } = insurance.pricingInfo;
  return (dispatch: ReduxDispatch, getState: GetReduxState): any => {
    const proposals = proposalsSelector(getState());
    // keep only proposals that have thoses services ids
    const proposalsWithServices = proposals.filter((p) =>
      Object.values(p.additionalServices)?.some(
        (services) =>
          Array.isArray(services) && services?.some(({ pricingInfo }) => id === pricingInfo?.id),
      ),
    );
    const validProposalIds = proposalsWithServices.map(({ id: proposalId }) => proposalId);
    return dispatch(
      addAdditionalService({
        proposalIds: validProposalIds,
        serviceId: id,
        attendeeIds,
        schedules: schedules.map(({ startDate, endDate }) => ({
          startDate,
          endDate,
        })),
        appContext,
      }),
    ).then((payload) => {
      dispatch(createAction(UPDATE_INSURANCES, payload));
      dispatch(createAction(INSURANCE_ADDED, id));
    });
  };
};

const deleteInsurances = ({
  insurance,
  attendeeIds,
  appContext,
}: AddOrRemoveInsuranceFromProposal) => {
  const insuranceId = insurance.pricingInfo.id;
  return (dispatch: ReduxDispatch, getState: GetReduxState): any => {
    const proposals = proposalsSelector(getState());
    // keep only proposals that have thoses services ids
    const proposalsWithServices = proposals.filter((p) =>
      Object.values(p.additionalServices)?.some(
        (services) =>
          Array.isArray(services) &&
          services?.some(({ pricingInfo }) => insuranceId === pricingInfo?.id),
      ),
    );
    const validProposalIds = proposalsWithServices.map(({ id }) => id);
    return dispatch(
      deleteAdditionalServices({
        proposalIds: validProposalIds,
        serviceIds: [insuranceId],
        attendeeIds,
        appContext,
      }),
    ).then((payload) => {
      dispatch(createAction(DELETE_INSURANCES, payload));
      dispatch(createAction(INSURANCE_DELETED, insuranceId));
    });
  };
};

/**
END INSURANCE
* */
const prepareBe = (payload: any) => createAction(PREPARE_BE, payload);

const saveDonationToProposals = (donation: FormattedPrice) => (dispatch: ReduxDispatch) => {
  dispatch({
    type: SAVE_DONATION_TO_PROPOSALS,
    payload: donation,
  });
};

const switchProposalsOffers =
  (offers: Array<string>, appContext: AppContext) =>
  (dispatch: ReduxDispatch, getState: GetReduxState) => {
    const state = getState();
    const proposals = proposalsSelector(state);
    // isolate proposals elligible for switching offers because offers are not for all proposals sadly
    const validProposals = proposals.filter(({ alternativePrice }) => {
      return (
        alternativePrice &&
        alternativePrice.discounts &&
        alternativePrice.discounts.length > 0 &&
        intersection(
          alternativePrice.discounts.map(({ offerId }) => offerId),
          offers,
        ).length > 0
      );
    });
    const proposalsIds = validProposals.map(({ id }) => id).filter((id) => !!id);
    return dispatch(
      createOptimisticAction(
        SWITCH_PROPOSALS_OFFERS,
        {},
        {
          optimist: true,
          promise: switchProposalsOffersMutation(proposalsIds, offers, appContext),
        },
      ),
    );
  };

export {
  applyProposalPromoCode,
  removeProposalPromoCode,
  setProposalAsAutosaved,
  addAccommodation,
  putTransportAndDispatchProposals,
  addAdditionalService,
  deleteAdditionalServices,
  updateAttendees,
  clearProposals,
  fetchProposalsAndRedirect,
  fetchProposalsById,
  fetchSelectedProposalPayment,
  saveCustomerProposalsHandler,
  saveCustomerProposalsOpportunityIdHandler,
  patchProposalHandler,
  deleteInsurances,
  addInsurances,
  refuseInsuranceAction,
  prepareBe,
  displayToastOnContextShare,
  editCriteriaFetchProposals,
  simpleFetchProposals,
  saveDonationToProposals,
  addOrReplaceAdditionalServices,
  switchProposalsOffers,
  getTransportSearchCriteria,
  fetchRefreshProposal,
};
